import { createSlice } from '@reduxjs/toolkit';
import type { TRootState } from 'slices/store';
import type { ILogState } from 'types/state.interface';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
  stateHasFetchedAllObjectsSuccess,
} from 'utils/sliceHelpers';
import type {
  ELoggedEventLevel,
  ELoggedEventType,
} from '@timeedit/types/lib/enums/loggedEvent.enum';
import type { TLoggedEvent } from '@timeedit/types/lib/types/loggedEvent.type';
import api from '../services/api.service';

export type TLoggedEventFindRequestData = {
  path: string;
  type: ELoggedEventType;
  level?: ELoggedEventLevel;
  start?: number;
  end?: number;
  tags?: string[];
  ids?: string[];
  sortOrder?: string;
  page?: number;
  limit?: number;
};

export const initialState: ILogState = {
  loading: false,
  hasErrors: false,
  results: [],
  map: {},
  page: 0,
  limit: 10000,
  totalPages: 0,
  totalResults: 0,
  tags: [],
};

const createLoggedEvent = (payload: any): TLoggedEvent => {
  return {
    id: payload.id,
    type: payload.type,
    level: payload.level,
    label: payload.label,
    items: payload.items,
    tags: payload.tags,
    createdAt: payload.createdAt,
    updatedAt: payload.updatedAt,
  };
};

// Slice
const slice = createSlice({
  name: 'logs',
  initialState,
  reducers: {
    fetchLogsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchLogsSuccess: (state, { payload }) => {
      stateHasFetchedAllObjectsSuccess(payload, state, createLoggedEvent);
      state.totalResults = payload.totalResults;
      finishedLoadingSuccess(state);
    },
    fetchLogsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    fetchTagsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchTagsSuccess: (state, { payload }) => {
      state.tags = payload;
    },
    fetchTagsFailure: () => {},
  },
});

export default slice.reducer;

// Selectors
export const logsLoading = (state: TRootState) => state.logs?.loading || false;
export const logsSelector = (state: TRootState) => {
  return state.logs?.results || [];
};
export const tagsSelector = (state: TRootState) => {
  return state.logs?.tags || [];
};
export const totalPagesSelector = (state: TRootState) => {
  return state.logs?.totalPages || 0;
};
export const totalResultsSelector = (state: TRootState) => {
  return state.logs?.totalResults || 0;
};

// Actions
export const {
  fetchLogsRequest,
  fetchLogsSuccess,
  fetchLogsFailure,
  fetchTagsRequest,
  fetchTagsSuccess,
  fetchTagsFailure,
} = slice.actions;

export const fetchLogs =
  (findLogRequestData: TLoggedEventFindRequestData) =>
  async (dispatch: any) => {
    const { path, ...data } = findLogRequestData;
    try {
      dispatch(fetchLogsRequest());
      const result = await api.post({
        endpoint: path,
        data,
        suppressNotification: true,
      });
      dispatch(fetchLogsSuccess(result));
    } catch (e) {
      dispatch(fetchLogsFailure());
      console.error(e);
    }
  };

export const fetchTags =
  (
    path: string,
    type: ELoggedEventType,
    level?: ELoggedEventLevel,
    start?: number,
    end?: number
  ) =>
  async (dispatch: any) => {
    const queryParams: Record<string, string> = {
      type,
      ...(level && { level }),
      ...(start && { start: start.toString() }),
      ...(end && { end: end.toString() }),
    };

    try {
      dispatch(fetchLogsRequest());
      const result = await api.get({
        endpoint: `${path}${
          queryParams ? `?${new URLSearchParams(queryParams)}` : ''
        }`,
      });
      dispatch(fetchTagsSuccess(result));
    } catch (e) {
      dispatch(fetchTagsFailure());
      console.error(e);
    }
  };
